import { useState } from "react";
import MessageTemplateList from "../MessageTemplateList";
import service from '../../service';
import { useModal } from "../../contexts/modal";
import BreadcrumbWrapper from "../BreadcrumbWrapper";
import Modal from '../Modal';
import MessageTemplateForm from '../MessageTemplateForm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { quickMessageQueryKeys } from '@Hooks/useQuickMessages';
import { Button } from "@art-of-hospitality-solutions-llc/ui-kit/components/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import ShowFor from '@Components/ShowFor';

const FALLBACK_DATA = [];
const QUICK_MESSAGE_DEFAULTS = { isQuickMessage: true, method: 'SMS' };
const TEMPLATES_TAB = 'templates';
const QUICK_MESSAGES_TAB = 'quick-messages';
const TEMPLATE_LIST_QUERY_KEY = ['messageTemplates', 'list'];

const MessageTemplates = () => {
  const [modalContent, setModalContent] = useState(null);
  const [tab, setTab] = useState(TEMPLATES_TAB);
  const { openModal, closeModal } = useModal();
  const isQuickMessageTab = tab === QUICK_MESSAGES_TAB;

  const queryClient = useQueryClient();
  const { data: messageTemplates } = useQuery({
    queryKey: [...TEMPLATE_LIST_QUERY_KEY, tab],
    queryFn: async () => {
      try {
        return (
          (await service.getMessageTemplates({
            quickMessage: isQuickMessageTab,
          })) || FALLBACK_DATA
        );
      } catch (error) {
        console.error(`Error fetching message templates:`, error);
        return FALLBACK_DATA;
      }
    },
  });

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: TEMPLATE_LIST_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: quickMessageQueryKeys.all });
  };

  const handleOpenModal = (content) => {
    setModalContent(content);
    openModal();
  };

  const handleCreateMessageTemplate = async (data) => {
    if (data.html && !data.messageBody) data.messageBody = 'html';
    try {
      const res = await service.createMessageTemplate(data);

      if (res.ok) {
        closeModal();
        refetch();
        setTab(data.isQuickMessage ? QUICK_MESSAGES_TAB : TEMPLATES_TAB);
      }
      return res;
    } catch (error) {
      console.error(error);
      return error;
    }

  };

  return (
    <div>
      <BreadcrumbWrapper crumbArray={[{ path: '/message-templates', label: 'Message Templates' }]} />

      <div className="mt-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold mb-4">Message Templates</h1>

          <ShowFor permissions={['MESSAGE_TEMPLATES_WRITE']}>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleOpenModal('createQuickMessage')}
              >
                Create Quick Message
              </Button>
              <Button onClick={() => handleOpenModal('create')}>
                Create Template
              </Button>
            </div>
          </ShowFor>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value={TEMPLATES_TAB}>Templates</TabsTrigger>
            <TabsTrigger value={QUICK_MESSAGES_TAB}>Quick Messages</TabsTrigger>
          </TabsList>
          {[TEMPLATES_TAB, QUICK_MESSAGES_TAB].map((value) => (
            <TabsContent key={value} value={value} className="mt-4">
              <MessageTemplateList
                modalContent={modalContent}
                setModalContent={setModalContent}
                messageTemplates={messageTemplates}
                refetch={refetch}
                isQuickMessage={value === QUICK_MESSAGES_TAB}
              />
            </TabsContent>
          ))}
        </Tabs>

      </div>

      {modalContent === 'create' && (
        <Modal className={"max-w-[80%]"}>
          <h2 className="mb-4 text-lg font-bold">Create Template</h2>
          <MessageTemplateForm
            onSubmitHandler={handleCreateMessageTemplate}
            title="Create Message Template"
            buttonText="Submit"
          />
        </Modal>
      )}
      {modalContent === 'createQuickMessage' && (
        <Modal className={"max-w-[80%]"}>
          <h2 className="mb-4 text-lg font-bold">Create Quick Message</h2>
          <MessageTemplateForm
            onSubmitHandler={handleCreateMessageTemplate}
            defaultValues={QUICK_MESSAGE_DEFAULTS}
          />
        </Modal>
      )}
    </div>
  );
};

export default MessageTemplates;
